import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Send, Loader2, MessageSquare, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * HelperWidget — the single, site-wide floating helper.
 *
 * Replaces the older Contact modal + header Contact button.  Opens a small
 * chat-style panel where visitors can:
 *   • Ask Wikifi anything (routes to the existing `/wikifi?q=…` flow —
 *     which uses whichever frontier model the user has selected).
 *   • Or fall back to sending a plain contact-form message via the same
 *     `/api/contact` endpoint that Resend already wires up.
 *
 * We deliberately keep the surface tiny so it never blocks page content.
 */
export function HelperWidget() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [mode, setMode] = useState("ask"); // "ask" | "contact"
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const nav = useNavigate();
  const panelRef = useRef(null);
  const { user } = useAuth();

  // Prefill email from the signed-in user if present.
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click / Escape when open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const askWikifi = () => {
    const v = q.trim();
    if (!v) return;
    setOpen(false);
    setQ("");
    nav(`/wikifi?q=${encodeURIComponent(v)}`);
  };

  const sendContact = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !msg.trim()) return;
    setBusy(true);
    try {
      await api.post("/contact", { email, message: msg, subject: "Helper widget" });
      setSent(true);
      setMsg("");
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send right now — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Trigger button — always visible in the bottom-right */}
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setSent(false); }}
        aria-label={open ? "Close helper" : "Open helper"}
        aria-expanded={open}
        title="Ask Wikifi"
        className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-[#0B1220] text-white shadow-lg hover:brightness-110 hover:shadow-xl active:translate-y-0.5 transition-all inline-flex items-center justify-center"
        data-testid="helper-widget-toggle"
      >
        {open ? <X size={18}/> : <Sparkles size={18}/>}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Wikifi helper"
          className="fixed bottom-20 right-5 z-50 w-[340px] max-w-[calc(100vw-40px)] rounded-xl bg-white border border-[#E7EAF0] shadow-xl overflow-hidden"
          data-testid="helper-widget-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#E7EAF0] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="font-display inline-flex items-center justify-center rounded-full text-white leading-none"
                style={{ width: 22, height: 22, background: "#000000", fontSize: 11, fontWeight: 700 }}
                aria-hidden="true"
              >
                W
              </span>
              <div>
                <div className="text-[12.5px] font-bold text-[#0B1220] leading-tight">Wikifi helper</div>
                <div className="text-[10px] text-[#5B6478]">Ask a question or send us a note.</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-md inline-flex items-center justify-center text-[#5B6478] hover:bg-[#F6F7FB] hover:text-[#0B1220]"
              aria-label="Close"
              data-testid="helper-widget-close"
            >
              <X size={13}/>
            </button>
          </div>

          {/* Mode tabs */}
          <div className="px-4 pt-3 flex items-center gap-1">
            <TabBtn active={mode === "ask"}     onClick={() => setMode("ask")}     icon={<Sparkles size={11}/>}       label="Ask Wikifi" testId="helper-mode-ask"/>
            <TabBtn active={mode === "contact"} onClick={() => setMode("contact")} icon={<MessageSquare size={11}/>} label="Contact us" testId="helper-mode-contact"/>
          </div>

          {/* Ask panel */}
          {mode === "ask" && (
            <form
              onSubmit={(e) => { e.preventDefault(); askWikifi(); }}
              className="p-4 space-y-3"
              data-testid="helper-ask-form"
            >
              <div className="text-[11.5px] text-[#5B6478] leading-relaxed">
                Anything you'd normally Google. Wikifi will answer using your
                selected frontier model.
              </div>
              <textarea
                value={q}
                onChange={(e) => setQ(e.target.value)}
                rows={3}
                placeholder="e.g. Which AI tool is best for making short reels?"
                className="w-full rounded-md bg-white border border-[#E7EAF0] px-3 py-2 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none focus:border-[#0B1220]/40 resize-none"
                data-testid="helper-ask-input"
              />
              <button
                type="submit"
                disabled={!q.trim()}
                className="w-full h-10 rounded-md bg-[#0B1220] text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                data-testid="helper-ask-submit"
              >
                Ask in Wikifi <ArrowUpRight size={12}/>
              </button>
            </form>
          )}

          {/* Contact panel */}
          {mode === "contact" && (
            sent ? (
              <div className="p-6 text-center" data-testid="helper-contact-sent">
                <div className="w-10 h-10 rounded-full bg-[#F7F8FA] border border-[#E7EAF0] inline-flex items-center justify-center mb-2">
                  <Send size={16} className="text-[#F97316]"/>
                </div>
                <div className="text-[13px] font-bold text-[#0B1220]">Message received.</div>
                <div className="text-[11.5px] text-[#5B6478] mt-1">We'll get back to {email} within two business days.</div>
              </div>
            ) : (
              <form onSubmit={sendContact} className="p-4 space-y-3" data-testid="helper-contact-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="w-full h-9 rounded-md bg-white border border-[#E7EAF0] px-3 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none focus:border-[#0B1220]/40"
                  data-testid="helper-contact-email"
                />
                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  rows={3}
                  placeholder="What can we help with?"
                  className="w-full rounded-md bg-white border border-[#E7EAF0] px-3 py-2 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none focus:border-[#0B1220]/40 resize-none"
                  data-testid="helper-contact-message"
                />
                <button
                  type="submit"
                  disabled={busy || !email.trim() || !msg.trim()}
                  className="w-full h-10 rounded-md bg-[#0B1220] text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  data-testid="helper-contact-submit"
                >
                  {busy ? <><Loader2 size={13} className="animate-spin"/> Sending…</> : <><Send size={12}/> Send</>}
                </button>
              </form>
            )
          )}
        </div>
      )}
    </>
  );
}

function TabBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest font-bold px-2 py-1 rounded transition-colors ${
        active ? "text-[#0B1220] border-b-2 border-[#0B1220]" : "text-[#8A93A6] hover:text-[#5B6478]"
      }`}
    >
      {icon} {label}
    </button>
  );
}
